import React, { useState, useEffect, useRef } from "react";
import classes from "./CompareInput.module.scss";
import { useNavigate } from "react-router-dom";
import inputHeader1 from "../../static/images/compare/input-header-1.png";
import inputHeader2 from "../../static/images/compare/input-header-2.png";
import cancelButton from "../../static/images/compare/cancel-image.png";
import TextForm, { TextFormResult } from "../../atoms/text-form/TextForm";
import SelectBox, { SelectBoxOption } from "../../atoms/select-box/SelectBox";
import SimpleButton from "../../atoms/simple-button/SimpleButton";
import { useLoginStatus } from "../../common/hooks/useLoginStatus";

// ★共通関数
import {
  digitsOnly,
  validatePostalCodeWith,
  validateIntegerWith,
} from "../../common/utility/number.util";

import {
  postUnregisteredUserCompare,
  postRegisteredUserCompare,
  PostUserCompareParam,
} from "../../api/UsersCompareApi";

import {
  postUnregisteredUsersSubmission,
  postRegisteredUsersSubmission,
} from "../../api/UsersSubmissionApi";
import { fetchUser } from "../../api/UsersApi";

type Form = {
  postAddress: string;
  annualSalary: string;
  birthYear: string;
  childrenCount: string;
  children: { order: number; grade: string }[];
};

const CompareInput = () => {
  const navigate = useNavigate();
  const { isLoggedIn } = useLoginStatus();

  const postSubmission = postRegisteredUsersSubmission();
  const postUnregisteredSubmission = postUnregisteredUsersSubmission();

  const postRegistered = postRegisteredUserCompare();
  const postUnregistered = postUnregisteredUserCompare();
  const userApi = fetchUser();

  const [formValue, setFormValue] = useState<Form>({
    postAddress: "",
    annualSalary: "",
    birthYear: "1996",
    childrenCount: "",
    children: [],
  });
  const hasInitializedUserForm = useRef(false);

  const childrenOptions = [
    { label: "0人", value: "0" },
    { label: "1人", value: "1" },
    { label: "2人", value: "2" },
    { label: "3人", value: "3" },
    { label: "4人", value: "4" },
    { label: "5人以上", value: "5" },
  ];

  const getChildrenNum = (v: string) => {
    if (!v) return 0;
    const n = Number(v);
    return Number.isFinite(n) ? Math.min(Math.max(n, 0), 5) : 0;
  };

  const buildChildrenFromColumns = (
    count: number,
    columns: (string | null | undefined)[]
  ) => {
    if (count === 0) return [];
    return columns.slice(0, count).map((grade, index) => ({
      order: index + 1,
      grade: grade ?? "",
    }));
  };

  const childrenNum = getChildrenNum(formValue.childrenCount);

  const onSelectChildren = (value: string) => {
    const n = getChildrenNum(value);

    setFormValue((prev) => {
      const nextChildren: { order: number; grade: string }[] = [];
      for (let i = 0; i < n; i++) {
        nextChildren.push({
          order: i + 1,
          grade: prev.children[i]?.grade || "",
        });
      }

      return {
        ...prev,
        childrenCount: value,
        children: nextChildren,
      };
    });
  };

  const CURRENT_YEAR = new Date().getFullYear();
  const buildYearOptions = (from: number, to: number): SelectBoxOption[] => {
    const arr: SelectBoxOption[] = [];
    for (let y = to; y >= from; y--) {
      arr.push({ name: String(y), value: String(y) });
    }
    return arr;
  };
  const yearOptions = buildYearOptions(1960, CURRENT_YEAR);

  const gradeOptions: SelectBoxOption[] = [
    { name: "未就学児", value: "preschool" },
    { name: "幼稚園 年少", value: "kinder1" },
    { name: "幼稚園 年中", value: "kinder2" },
    { name: "幼稚園 年長", value: "kinder3" },
    { name: "小学校 1年生", value: "primary1" },
    { name: "小学校 2年生", value: "primary2" },
    { name: "小学校 3年生", value: "primary3" },
    { name: "小学校 4年生", value: "primary4" },
    { name: "小学校 5年生", value: "primary5" },
    { name: "小学校 6年生", value: "primary6" },
    { name: "中学校 1年生", value: "junior1" },
    { name: "中学校 2年生", value: "junior2" },
    { name: "中学校 3年生", value: "junior3" },
    { name: "高校 1年生", value: "high1" },
    { name: "高校 2年生", value: "high2" },
    { name: "高校 3年生", value: "high3" },
    { name: "大学 1年生", value: "uni1" },
    { name: "大学 2年生", value: "uni2" },
    { name: "大学 3年生", value: "uni3" },
    { name: "大学 4年生", value: "uni4" },
  ];

  // 表示だけハイフン（保存は数字）
  const formatZip = (v: string) => {
    const digits = digitsOnly(v, 7);
    if (digits.length <= 3) return digits;
    return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  };

  // ★郵便番号チェック（必須）
  const zipValidator = (v: string) =>
    validatePostalCodeWith(v, { allowEmpty: false, label: "郵便番号" });
  
  const isZipOk = digitsOnly(formValue.postAddress, 7).length === 7;
  const isChildrenOk = formValue.childrenCount !== "";
  const hasSelectedGrade = (grade?: string) =>
    grade !== undefined && grade.trim() !== "" && grade !== "NA";
  const isChildrenGradeOk =
    childrenNum === 0 ||
    (formValue.children.length >= childrenNum &&
      formValue.children
        .slice(0, childrenNum)
        .every((child) => hasSelectedGrade(child.grade)));

  // 年収は validateIntegerWith をそのまま使って判定
  const salaryDigits = digitsOnly(formValue.annualSalary, 5);
  
  const isSalaryOk = salaryDigits.length > 0 && Number(salaryDigits) >= 1;

  const canSubmit = isZipOk && isChildrenOk && isChildrenGradeOk && isSalaryOk;

  const hasPostedCompare = useRef(false); // ★ compare POST 用

  useEffect(() => {
    if (isLoggedIn) {
      userApi.fetch();
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn || hasInitializedUserForm.current) return;
    if (userApi.state?.status !== "success") return;

    const user = userApi.state.data?.[0];
    if (!user) return;

    const childrenCountNum = getChildrenNum(
      user.childrenCount === null || user.childrenCount === undefined
        ? ""
        : String(user.childrenCount)
    );
    const childrenCount =
      user.childrenCount === null || user.childrenCount === undefined
        ? ""
        : String(childrenCountNum);
    const childrenColumns = [
      user.childrenColumn1,
      user.childrenColumn2,
      user.childrenColumn3,
      user.childrenColumn4,
      user.childrenColumn5,
    ];

    setFormValue({
      postAddress: digitsOnly(user.postalCode ?? "", 7),
      annualSalary:
        user.annualSalary === null || user.annualSalary === undefined
          ? ""
          : digitsOnly(String(user.annualSalary), 5),
      birthYear: user.birthYear ?? "1996",
      childrenCount,
      children: buildChildrenFromColumns(childrenCountNum, childrenColumns),
    });
    hasInitializedUserForm.current = true;
  }, [isLoggedIn, userApi.state]);

  const handleStart = () => {
    if (!canSubmit) return;

    const payload: PostUserCompareParam = {
      postalCode: formatZip(formValue.postAddress),
      childrenCount: formValue.childrenCount,
      birthYear: formValue.birthYear,
      annualSalary: formValue.annualSalary,
      children: formValue.children,
    };

    if (isLoggedIn) {
      postRegistered.post(payload);
    } else {
      postUnregistered.post(payload);
    }
    hasPostedCompare.current = true;
  };
  useEffect(() => {
    if (!hasPostedCompare.current) return;

    if (isLoggedIn) {
      if (
        postRegistered.state?.data != null &&
        postRegistered.state?.status === "success"
      ) {
        navigate("/comparenavi/result");
      }
    } else {
      if (
        postUnregistered.state?.data != null &&
        postUnregistered.state?.status === "success"
      ) {
        navigate("/comparenavi/result");
      }
    }
  }, [postRegistered.state, postUnregistered.state, isLoggedIn, navigate]);

  useEffect(() => {
    if (isLoggedIn === null) return; // ★nullの間に走らせない
    if (isLoggedIn) {
      postSubmission.post({ simulationType: "compare-official" });
    } else {
      postUnregisteredSubmission.post({ simulationType: "compare-light" });
    }
  }, [isLoggedIn]);

  const navigateCancelDestination = () => {
    if (isLoggedIn) {
      navigate("/comparenavi/result");
      return;
    }
    navigate("/comparenavi");
  };

  return (
    <div className={classes.container}>
      <div className={classes.body}>
        <div className={classes.inputContainer}>
          <img
            src={cancelButton}
            alt="キャンセル"
            className={classes.cancelImage}
            role="button"
            tabIndex={0}
            onClick={navigateCancelDestination}
          />
          <img src={inputHeader1} alt="比較ナビ 入力ヘッダー1" />
          <img src={inputHeader2} alt="比較ナビ 入力ヘッダー2" />

          <div className={classes.questionHeader}>Q1.お住まいの郵便番号は？</div>
          <div className={classes.questionInput}>
            <TextForm
              name="postAddress"
              value={formatZip(formValue.postAddress)}
              color="white"
              prefix="〒"
              placeholder="100-0000"
              inputWidth="70%"
              maxLength={8}
              validator={zipValidator}
              onChange={(args) => {
                // ★保存は数字7桁だけ（共通関数）
                const digits = digitsOnly(args.value, 7);
                setFormValue((prev) => ({ ...prev, postAddress: digits }));
              }}
            ></TextForm>
          </div>

          <div className={classes.questionHeader}>Q2.お子さまは何人いる？</div>
          <div className={classes.questionInput}>
            <div
              className={classes.childrenButtons}
              role="group"
              aria-label="お子さまの人数"
            >
              {childrenOptions.map((opt) => {
                const selected = formValue.childrenCount === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    className={`${classes.childrenButton} ${
                      selected ? classes.selected : ""
                    }`}
                    onClick={() => onSelectChildren(opt.value)}
                    aria-pressed={selected}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {childrenNum > 0 && (
            <div className={classes.childYearBlock}>
              {Array.from({ length: childrenNum }).map((_, idx) => (
                <div className={classes.childItem} key={`child-year-${idx}`}>
                  <div className={classes.childHeader}>
                    {idx + 1}人目のお子さまは何学年？
                  </div>
                  <div className={classes.childInput}>
                    <div className={classes.yearSelectRow}>
                      <SelectBox
                        id={`childGrade-${idx}`}
                        title={{ name: "", value: `childGrade-${idx}` }}
                        defaultValue={formValue.children[idx]?.grade || ""}
                        selectWidth="86%"
                        option={gradeOptions}
                        vertical
                        onChange={(args) => {
                          setFormValue((prev) => {
                            const next = [...prev.children];
                            if (next[idx]) {
                              next[idx].grade = args.value;
                            }
                            return { ...prev, children: next };
                          });
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className={classes.questionHeader}>
            Q3.世帯主の生まれた年は？
            <div className={classes.questionInput}>
              <div className={classes.yearSelectRow}>
                <SelectBox
                  id="birthYear"
                  title={{ name: "", value: "birthYear" }}
                  defaultValue={formValue.birthYear || "NA"}
                  selectWidth="86%"
                  option={yearOptions}
                  onChange={(args) =>
                    setFormValue((prev) => ({ ...prev, birthYear: args.value }))
                  }
                  suffix="年"
                  vertical
                />
              </div>
            </div>
          </div>

          <div className={classes.questionHeader}>
            Q4.世帯年収は？<br />
            ※世帯年収が近い家庭と比較できます
          </div>
          <div className={classes.questionInput}>
            <TextForm
              name="annualSalary"
              value={formValue.annualSalary}
              color="white"
              inputWidth="70%"
              maxLength={5} // 0〜99999の想定なら5桁
              align="right"
              suffix="万 円"
              validator={(v) =>
                validateIntegerWith(v, {
                  allowEmpty: false,
                  min: 0,
                  max: 99999,
                  label: "世帯年収",
                })
              }
              onChange={(args) => {
                const digits = digitsOnly(args.value, 5);
                setFormValue((prev) => ({ ...prev, annualSalary: digits }));
              }}
            />
          </div>

          <div className={classes.questionHeader}></div>
          <SimpleButton
            width={"50%"}
            label={"これでOK!"}
            onClick={handleStart}
            disabled={!canSubmit}
          />
        </div>
      </div>
    </div>
  );
};

export default CompareInput;
